import React, {useState, useEffect, useRef} from 'react';
import {Stack, Grid, Collapse, Typography} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import '../FieldTypes.scss';
import {ISelectItem} from '../../../../../../shared/types';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrNumericInput from '../../../../../common/arr-numeric-input/ArrNumericInput';
import ArrDatePicker from '../../../../../common/arr-date-picker/ArrDatePicker';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrAutocomplete from '../../../../../common/arr-autocomplete/ArrAutocomplete';
import {Field} from '../../../../types/Field';
import {FieldTypeProps} from '../FieldTypeProps';
import {
  useFieldData,
  useAutocomplete,
} from '../../../../../../hooks/session-search/useSessionSearch';
import {formatAutocompleteResults} from '../../autocompleteUtils';
import {
  getStartDateTime,
  getEndDateTime,
  validateStartDate,
  validateStartTime,
  validateStartDateTime,
  validateDateRange,
  validateTimeRange,
  validateDateTimeRange,
  IValidationResult,
} from './metadataFieldTypeUtils';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm:ss';

const operatorItems = [
  {
    value: 'is',
    label: t('sessionSearch.queryList.contentAndMetadata.metadataDrawer.is'),
  },
  {
    value: 'range',
    label: t('sessionSearch.queryList.contentAndMetadata.metadataDrawer.range'),
  },
];

const booleanItems = [
  {
    value: 'true',
    label: t('sessionSearch.queryList.contentAndMetadata.metadataDrawer.true'),
  },
  {
    value: 'false',
    label: t('sessionSearch.queryList.contentAndMetadata.metadataDrawer.false'),
  },
];

const MetadataFieldType: React.FC<FieldTypeProps> = ({
  type,
  onCriteriaChaged,
  onValidated,
}) => {
  const [fields, setFields] = useState<Field[]>([]);
  const [currentField, setCurrentField] = useState<Field | null>(null);
  const [currentOperator, setCurrentOperator] = useState<string>('is');
  const [currentValues, setCurrentValues] = useState<string[]>([]);

  const [fieldSelectItems, setFieldSelectItems] = useState<ISelectItem[]>([]);
  const [fieldSelectedItem, setFieldSelectedItem] =
    useState<ISelectItem | null>(null);
  const [operatorSelectItems] = useState<ISelectItem[]>(operatorItems);
  const [operatorSelectedItem, setOperatorSelectedItem] =
    useState<ISelectItem | null>(operatorItems[0]);
  const [booleanSelectItems] = useState<ISelectItem[]>(booleanItems);
  const [booleanSelectedItem, setBooleanSelectedItem] =
    useState<ISelectItem | null>(null);
  const [startNumericValue, setStartNumericValue] = useState<number | null>();
  const [endNumericValue, setEndNumericValue] = useState<number | null>();
  const [textAreaValue, setTextAreaValue] = useState<string>('');
  const [startDateValue, setStartDateValue] = useState<string | null>(null);
  const [endDateValue, setEndDateValue] = useState<string | null>(null);
  const [startTimeValue, setStartTimeValue] = useState<string | null>(null);
  const [endTimeValue, setEndTimeValue] = useState<string | null>(null);

  const [isHelpExpanded, setIsHelpExpanded] = useState<boolean>(false);

  const [autocompleteValue, setAutocompleteValue] = useState<string>('');
  const [autocompleteEnabled, setAutocompleteEnabled] =
    useState<boolean>(false);
  const [autocompleteItems, setAutocompleteItems] = useState<string[]>([]);
  const {data: autocompleteData, refetch} = useAutocomplete(autocompleteValue);

  const {data: fieldData} = useFieldData(type);

  const [numberRangeError, setNumberRangeError] = useState<boolean>(false);
  const [numberErrorText, setNumberErrorText] = useState<string>('');

  const [startDateError, setStartDateError] = useState(false);
  const [startDateHelperText, setStartDateHelperText] = useState('');

  const [endDateError, setEndDateError] = useState(false);
  const [endDateHelperText, setEndDateHelperText] = useState('');

  const [startTimeError, setStartTimeError] = useState(false);
  const [startTimeHelperText, setStartTimeHelperText] = useState('');

  const [endTimeError, setEndTimeError] = useState(false);
  const [endTimeHelperText, setEndTimeHelperText] = useState('');

  const startTimeRef = useRef<HTMLDivElement>(null);
  const endTimeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fieldData?.workProductItemsDetails) return;

    const fieldsFromApi: Field[] = fieldData.workProductItemsDetails.map(
      (item) => ({
        id: item.id,
        text: item.text,
        type: item.type,
        isRange:
          item.type === 'INT' ||
          item.type === 'DATE' ||
          item.type === 'TIME' ||
          item.type === 'DATETIME',
      })
    );

    setFields(fieldsFromApi);

    const selectItems = fieldsFromApi.map((f) => ({
      value: f.id,
      label: f.text,
    }));
    setFieldSelectItems(selectItems);
  }, [fieldData]);

  useEffect(() => {
    // Set FIELD when one is selected from the list
    let field: Field | null = null;
    if (fieldSelectedItem) {
      field = fields.find((f) => f.id === fieldSelectedItem.value) ?? null;
      setCurrentField(field);
      setOperatorSelectedItem(operatorItems[0]); // default to "is"
      setBooleanSelectedItem(null);
      setStartNumericValue(null);
      setEndNumericValue(null);
      setTextAreaValue('');
      setStartDateValue(null);
      setEndDateValue(null);
      setStartTimeValue(null);
      setEndTimeValue(null);
      onValidated?.(false);
    }
  }, [fieldSelectedItem]);

  useEffect(() => {
    // Set OPERATOR when one is selected from the list
    let operator: string | null = null;
    if (operatorSelectedItem) {
      const item =
        operatorSelectItems.find(
          (value) => value.value === operatorSelectedItem.value
        ) ?? null;
      if (item && item.value) {
        operator = item.value.toString();
        setCurrentOperator(operator);

        // Reset the UI for a new OPERATOR
        setEndNumericValue(null);
        setEndDateValue(null);
        setEndTimeValue(null);

        if (operator === 'range') {
          onValidated && onValidated(false);
        }
      }
    }
  }, [operatorSelectedItem]);

  useEffect(() => {
    // Set VALUES to TEXT input
    if (currentField) {
      setCurrentValues([textAreaValue]);
      if (textAreaValue && textAreaValue.length >= 3) {
        setAutocompleteValue(`${currentField.text}:${textAreaValue}`);
      }
      onValidated && onValidated(textAreaValue ? true : false);
    }
  }, [textAreaValue]);

  useEffect(() => {
    refetch();
  }, [autocompleteValue]);

  useEffect(() => {
    // Set VALUES to BOOLEAN input
    if (currentField) {
      const item = booleanSelectedItem?.value ?? null;
      setCurrentValues([`${item}`]);
      onValidated && onValidated(item ? true : false);
    }
  }, [booleanSelectedItem]);

  useEffect(() => {
    // Set VALUES to DATE, TIME, or DATETIME input
    setStartDateError(false);
    setStartTimeError(false);
    setEndDateError(false);
    setEndTimeError(false);

    if (currentField) {
      const tempValues = [...currentValues];
      tempValues[0] = getStartDateTime(startDateValue, startTimeValue);
      tempValues[1] = getEndDateTime(endDateValue, endTimeValue);
      setCurrentValues(tempValues);

      let validationResult: IValidationResult = {
        isValid: false,
        validations: ['Success'],
      };

      if (currentField.type === 'DATE') {
        if (currentOperator === 'range') {
          validationResult = validateDateRange(startDateValue, endDateValue);
        } else {
          validationResult = validateStartDate(startDateValue);
        }
      } else if (currentField.type === 'TIME') {
        if (currentOperator === 'range') {
          validationResult = validateTimeRange(startTimeValue, endTimeValue);
        } else {
          validationResult = validateStartTime(startTimeValue);
        }
      } else if (currentField.type === 'DATETIME') {
        if (currentOperator === 'range') {
          validationResult = validateDateTimeRange(
            startDateValue,
            startTimeValue,
            endDateValue,
            endTimeValue
          );
        } else {
          validationResult = validateStartDateTime(
            startDateValue,
            startTimeValue
          );
        }
      }

      if (!validationResult.isValid) {
        showDateTimeError(validationResult);
      }

      onValidated && onValidated(validationResult.isValid);
    }
  }, [startDateValue, endDateValue, startTimeValue, endTimeValue]);

  useEffect(() => {
    // Set VALUES to NUMERIC input
    if (currentField) {
      const tempValues = [...currentValues];
      if (startNumericValue || startNumericValue === 0) {
        tempValues[0] = startNumericValue.toString();
      }
      if (endNumericValue || endNumericValue === 0) {
        tempValues[1] = endNumericValue.toString();
      }
      setCurrentValues(tempValues);

      if (currentOperator === 'range') {
        setNumberRangeError(false);
        setNumberErrorText('');

        const hasStart = startNumericValue != null;
        const hasEnd = endNumericValue != null;
        const hasRange = hasStart && hasEnd;

        let startComesBeforeEnd = false;
        if (hasRange) {
          startComesBeforeEnd = endNumericValue >= startNumericValue;
        }

        // A range is provided but the start and end are not valid
        if (hasRange && !startComesBeforeEnd) {
          setNumberRangeError(true);
          setNumberErrorText(
            t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.numberRangeError'
            )
          );
        }

        // Start and End numbers are required for range
        onValidated && onValidated(hasRange && startComesBeforeEnd);
      } else {
        onValidated && onValidated(startNumericValue != null);
      }
    }
  }, [startNumericValue, endNumericValue]);

  useEffect(() => {
    // Call onCriteriaChaged anytime the FIELD, OPERATOR, or VALUES change
    if (currentField) {
      onCriteriaChaged &&
        onCriteriaChaged(currentField, currentOperator, currentValues);
    }
  }, [currentField, currentOperator, currentValues]);

  useEffect(() => {
    const suggestions = formatAutocompleteResults(autocompleteData ?? []);
    setAutocompleteItems(suggestions);
  }, [autocompleteData]);

  const showDateTimeError = (validationResult: IValidationResult) => {
    if (validationResult.validations.some((i) => i === 'Success')) {
      return;
    }

    if (
      validationResult.validations.some((i) => i === 'Missing required field')
    ) {
      return;
    }

    for (const validation of validationResult.validations) {
      switch (validation) {
        case 'Invalid range - date':
          setEndDateError(true);
          setEndDateHelperText(t('arrFilterAccordion.endDateAfterStartDate'));
          break;
        case 'Invalid range - time':
          setEndTimeError(true);
          setEndTimeHelperText(t('arrFilterAccordion.endDateAfterStartDate'));
          break;
        case 'Invalid format - start date':
          setStartDateError(true);
          setStartDateHelperText(
            t('common.arrDatePicker.singleDateErrorHyphen')
          );
          break;
        case 'Invalid format - start time':
          setStartTimeError(true);
          setStartTimeHelperText(t('arrFilterAccordion.invalidTimeError'));
          break;
        case 'Invalid format - end date':
          setEndDateError(true);
          setEndDateHelperText(t('common.arrDatePicker.singleDateErrorHyphen'));
          break;
        case 'Invalid format - end time':
          setEndTimeError(true);
          setEndTimeHelperText(t('arrFilterAccordion.invalidTimeError'));
          break;
      }
    }
  };

  const renderSelection = () => {
    let component = <></>;

    if (fieldSelectedItem) {
      switch (currentField?.type) {
        case 'NVARCHAR':
          component = renderTextComponent();
          break;
        case 'INT':
          component = renderNumberComponent();
          break;
        case 'BIT':
          component = renderBooleanComponent();
          break;
        case 'DATE':
          component = renderDateComponent();
          break;
        case 'TIME':
          component = renderTimeComponent();
          break;
        case 'DATETIME':
          component = renderDateTimeComponent();
          break;
        default:
          component = renderTextComponent();
          break;
      }
    }

    return component;
  };

  const renderDateComponent = () => {
    return (
      <Grid container spacing={2}>
        <Grid size="grow" className="range-start">
          <ArrDatePicker
            type="date-picker"
            label={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.startDate'
            )}
            onChange={(value) => setStartDateValue(value as string)}
            initialStartDate={startDateValue ?? undefined}
            placeholder={t('common.arrDatePicker.dateFormatHyphen')}
            applyEnable={true}
            dataTestId="date-field-start"
            isError={startDateError}
            helperText={startDateHelperText}
            dateFormat={DATE_FORMAT}
          />
        </Grid>
        <Grid className="range-separator">
          <RemoveIcon className="range-separator-icon" />
        </Grid>
        <Grid size="grow" className="range-end">
          <ArrDatePicker
            type="date-picker"
            label={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.endDate'
            )}
            onChange={(value) => setEndDateValue(value as string)}
            initialEndDate={endDateValue ?? undefined}
            placeholder={t('common.arrDatePicker.dateFormatHyphen')}
            applyEnable={true}
            dataTestId="date-field-end"
            disabled={currentOperator !== 'range'}
            isError={endDateError}
            helperText={endDateHelperText}
            dateFormat={DATE_FORMAT}
          />
        </Grid>
      </Grid>
    );
  };

  const renderTimeComponent = () => {
    // Append the (optional) text if date and time are selected
    let startLabel = t(
      'sessionSearch.queryList.contentAndMetadata.metadataDrawer.startTime'
    );
    let endLabel = t(
      'sessionSearch.queryList.contentAndMetadata.metadataDrawer.endTime'
    );
    const optionalLabel = t(
      'sessionSearch.queryList.contentAndMetadata.metadataDrawer.optional'
    );

    const isDateTime = currentField?.type === 'DATETIME';
    if (isDateTime) {
      startLabel = `${startLabel} ${optionalLabel}`;
      endLabel = `${endLabel} ${optionalLabel}`;
    }

    return (
      <Grid container spacing={2}>
        <Grid size="grow" className="range-start" ref={startTimeRef}>
          <ArrDatePicker
            type="date-picker"
            label={startLabel}
            placeholder="00:00:00"
            onChange={(value) => setStartTimeValue(value as string)}
            applyEnable={true}
            mode="time"
            popperAlign="end"
            anchorOverrideRef={startTimeRef}
            ampm={false}
            dataTestId="time-field-start"
            isError={startTimeError}
            helperText={startTimeHelperText}
            timeFormat={TIME_FORMAT}
          />
        </Grid>
        <Grid className="range-separator">
          <RemoveIcon className="range-separator-icon" />
        </Grid>
        <Grid size="grow" className="range-end" ref={endTimeRef}>
          <ArrDatePicker
            type="date-picker"
            label={endLabel}
            placeholder="24:00:00"
            onChange={(value) => setEndTimeValue(value as string)}
            applyEnable={true}
            mode="time"
            popperAlign="end"
            anchorOverrideRef={endTimeRef}
            ampm={false}
            dataTestId="time-field-end"
            disabled={currentOperator !== 'range'}
            isError={endTimeError}
            helperText={endTimeHelperText}
            timeFormat={TIME_FORMAT}
          />
        </Grid>
      </Grid>
    );
  };

  const renderDateTimeComponent = () => {
    return (
      <Stack spacing={2}>
        {renderDateComponent()}
        {renderTimeComponent()}
      </Stack>
    );
  };

  const renderBooleanComponent = () => {
    return (
      <Grid container spacing={2}>
        <Grid size="grow" className="range-start">
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.selectValue'
            )}
            dropdownItemKey="value"
            dropdownItemDisplay="label"
            dropdownItems={booleanSelectItems}
            selectedValues={booleanSelectedItem}
            onSelectionChange={setBooleanSelectedItem}
            sizeVariant="large"
            dataTestId="asd-metadata-field-boolean-select"
          />
        </Grid>
        <Grid className="range-separator"></Grid>
        <Grid size="grow" className="range-end"></Grid>
      </Grid>
    );
  };

  const renderTextComponent = () => {
    return (
      <Grid>
        <ArrAutocomplete
          value={textAreaValue}
          options={autocompleteItems}
          onChange={setTextAreaValue}
          enabled={autocompleteEnabled}
          data-testid="metadata-field-type-autocomplete"
        >
          <ArrTextArea
            value={textAreaValue}
            placeholder={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.enterValue'
            )}
            rows={6}
            onChange={setTextAreaValue}
            onFocus={() => setAutocompleteEnabled(true)}
            onBlur={() => setAutocompleteEnabled(false)}
            resizable={true}
            dataTestId={`text-area-session-meta-text`}
          />
        </ArrAutocomplete>
      </Grid>
    );
  };

  const renderNumberComponent = () => {
    return (
      <Grid container spacing={2}>
        <Grid size="grow" className="range-start">
          <ArrNumericInput
            value={startNumericValue ?? undefined}
            label={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.startNumber'
            )}
            onChange={setStartNumericValue}
            sizeVariant="large"
            dataTestId="number-field-start"
            showPercentage={false}
          />
        </Grid>
        <Grid className="range-separator">
          <RemoveIcon className="range-separator-icon" />
        </Grid>
        <Grid size="grow" className="range-end">
          <ArrNumericInput
            value={endNumericValue ?? undefined}
            label={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.endNumber'
            )}
            onChange={setEndNumericValue}
            sizeVariant="large"
            dataTestId="number-field-end"
            showPercentage={false}
            disabled={currentOperator !== 'range'}
            isError={numberRangeError}
            supportText={numberErrorText}
          />
        </Grid>
      </Grid>
    );
  };

  const renderOperator = () => {
    let component = <></>;

    if (fieldSelectedItem && currentField?.isRange) {
      component = (
        <ArrSingleSelectDropdown
          dropdownLabel={t(
            'sessionSearch.queryList.contentAndMetadata.metadataDrawer.selectOperator'
          )}
          dropdownItemKey="value"
          dropdownItemDisplay="label"
          dropdownItems={operatorSelectItems}
          selectedValues={operatorSelectedItem}
          onSelectionChange={setOperatorSelectedItem}
          sizeVariant="large"
          dataTestId="asd-metadata-field-operator-select"
        />
      );
    }

    return component;
  };

  return (
    <Stack spacing={2} className="field-type-content">
      <Grid container spacing={2} className="field-type-selection">
        <Grid size="grow" className="field-selection">
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.selectField'
            )}
            dropdownItemKey="value"
            dropdownItemDisplay="label"
            dropdownItems={fieldSelectItems}
            selectedValues={fieldSelectedItem}
            onSelectionChange={setFieldSelectedItem}
            sizeVariant="large"
            dataTestId="asd-metadata-field-selected-item"
            searchable
            showNoResultsText
          />
        </Grid>
        <Grid className="field-separator"></Grid>
        <Grid size="grow" className="field-operator">
          {renderOperator()}
        </Grid>
      </Grid>
      <Grid className="field-type-value">{renderSelection()}</Grid>
      <Grid className="field-type-help">
        <Stack spacing={2}>
          <ArrButton
            variant="text"
            startIcon={<InfoOutlinedIcon />}
            endIcon={
              isHelpExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
            }
            onClick={() => setIsHelpExpanded((v) => !v)}
            size="small"
            className="metadata-search-help"
            dataTestId="metadata-search-help"
          >
            {t(
              'sessionSearch.queryList.contentAndMetadata.metadataDrawer.searchHelpLabel'
            )}
          </ArrButton>
          <Collapse in={isHelpExpanded}>
            <Typography
              className="typo-text body5_size"
              data-testid={`metadata-field-type-typography`}
            >
              <Trans i18nKey="sessionSearch.queryList.contentAndMetadata.metadataDrawer.searchHelpContent" />
            </Typography>
          </Collapse>
        </Stack>
      </Grid>
    </Stack>
  );
};

export default MetadataFieldType;
